import { createStyles, makeStyles } from "@material-ui/styles";
import * as React from "react";

import {
    Breakpoint,
    Button,
    CandidateAddEditInterviewDialog,
    CandidateInterviewNotificationDialog,
    CandidateInterviewType,
    LayoutContainer,
    PlusIcon,
    RelodeTheme,
    Text,
} from "../../..";
import * as domain from "../../../domain";
import { Stakeholder } from "../../../domain";
import { CandidateInterviewDetailsDialog } from "../candidateInterviewDetailsDialog";
import { SubmitInterviewType } from "../candidateInterviewProps";
import { CandidateJobInterviewListDesktop } from "./candidateJobInterviewListDesktop";
import { CandidateJobInterviewListMobile } from "./candidateJobInterviewListMobile";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            interviewsSection: {
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
            },

            button: {
                [theme.breakpoints.up("md")]: {
                    width: 180,
                },
                marginBottom: theme.spacing(3),
            },
            interviewListSection: {
                marginBottom: theme.spacing(4),
            },
            headerAction: {
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
            },
        }),
    { name: "CandidateInterview" }
);

interface CandidateJobInterviewsProps {
    candidateUserId: number;
    candidateRoleName: string;
    candidateFullName: string;
    candidateFirstName: string;
    candidateManagedBy?: Stakeholder;
    jobName: string;
    jobId: number;
    companyName: string;
    companyBackground: string;
    jobJobManager: Stakeholder;
    pastInterviews: domain.CandidateInterview[];
    upcomingInterviews: domain.CandidateInterview[];
    canEditInterview: boolean;
    canAddInterview: boolean;
    onSubmitInterview: SubmitInterviewType;
    handleSendManualInterviewNotification: (
        interviewId: number,
        subject: string,
        recipientUserIds: number[]
    ) => Promise<boolean>;
    isCandidateLoading: boolean;
}

export const CandidateJobInterviews = (props: CandidateJobInterviewsProps) => {
    const classes = useStyles({});
    const {
        candidateFullName,
        candidateFirstName,
        candidateUserId,
        candidateRoleName,
        candidateManagedBy,
        jobName,
        jobId,
        companyName,
        companyBackground,
        jobJobManager,
        upcomingInterviews,
        pastInterviews,
        canEditInterview,
        canAddInterview,
        isCandidateLoading,
        onSubmitInterview,
        handleSendManualInterviewNotification,
    } = props;

    const [
        currentInterview,
        setCurrentInterview,
    ] = React.useState<domain.CandidateInterview>(undefined);
    const [
        isAddEditInterviewModalOpen,
        setIsAddEditInterviewModalOpen,
    ] = React.useState(false);
    const [
        isInterviewDetailsModalOpen,
        setIsInterviewDetailsModalOpen,
    ] = React.useState(false);
    const [
        isInterviewNotificationDialogOpen,
        setIsInterviewNotificationDialogOpen,
    ] = React.useState(false);

    const handleAddEditInterviewDialogClose = () => {
        setIsAddEditInterviewModalOpen(false);
    };

    const handleInterviewComplete = () => {
        setIsAddEditInterviewModalOpen(false);
    };

    const handleAddEditInterviewDialogOpen = (
        interview?: domain.CandidateInterview
    ) => {
        setCurrentInterview(interview);
        setIsAddEditInterviewModalOpen(true);
    };

    const handleInterviewDetailsDialogOpen = (
        interview: domain.CandidateInterview
    ) => {
        setCurrentInterview(interview);
        setIsInterviewDetailsModalOpen(true);
    };

    const handleTransitionFromDetailsToNotificationDialogs = () => {
        //closing the details dialog when the notification dialog closes
        //allows for a smoother transition in ui. setting that boolean
        //here causes a re-render and a visible flicker
        setIsInterviewNotificationDialogOpen(true);
    };

    const handleInterviewDetailsDialogClose = () => {
        setIsInterviewDetailsModalOpen(false);
    };

    const handleInterviewNotificationDialogOpen = (
        interview: domain.CandidateInterview
    ) => {
        setCurrentInterview(interview);
        setIsInterviewNotificationDialogOpen(true);
    };

    const handleInterviewNotificationDialogClose = () => {
        setIsInterviewNotificationDialogOpen(false);
        setIsInterviewDetailsModalOpen(false);
    };

    return (
        <LayoutContainer>
            <div className={classes.interviewsSection}>
                {canAddInterview ? (
                    <Button
                        id="addInterviewButton"
                        size="small"
                        color="default"
                        variant="contained"
                        onClick={() =>
                            handleAddEditInterviewDialogOpen(undefined)
                        }
                        title="add interview"
                        className={classes.button}
                        startIcon={<PlusIcon />}
                    >
                        <span>interview</span>
                    </Button>
                ) : null}

                {upcomingInterviews.length > 0 && (
                    <div className={classes.interviewListSection}>
                        <div className={classes.headerAction}>
                            <Text variant="h5">upcoming interviews</Text>
                        </div>
                        <Breakpoint desktop>
                            <CandidateJobInterviewListDesktop
                                interviewList={upcomingInterviews}
                                handleOpenEditInterview={
                                    canEditInterview
                                        ? handleAddEditInterviewDialogOpen
                                        : undefined
                                }
                                handleOpenInterviewDetails={
                                    handleInterviewDetailsDialogOpen
                                }
                                handleOpenInterviewNotificationDialog={
                                    handleInterviewNotificationDialogOpen
                                }
                                dataCy="upcomingInterviews"
                            />
                        </Breakpoint>
                        <Breakpoint mobile>
                            <CandidateJobInterviewListMobile
                                interviewList={upcomingInterviews}
                                handleOpenEditInterview={
                                    canEditInterview
                                        ? handleAddEditInterviewDialogOpen
                                        : undefined
                                }
                                handleOpenInterviewDetails={
                                    handleInterviewDetailsDialogOpen
                                }
                                handleOpenInterviewNotificationDialog={
                                    handleInterviewNotificationDialogOpen
                                }
                                dataCy="upcomingInterviews"
                            />
                        </Breakpoint>
                    </div>
                )}
                {pastInterviews.length > 0 && (
                    <div className={classes.interviewListSection}>
                        <div className={classes.headerAction}>
                            <Text variant="h5">past interviews</Text>{" "}
                        </div>
                        <Breakpoint desktop>
                            <CandidateJobInterviewListDesktop
                                interviewList={pastInterviews}
                                handleOpenEditInterview={
                                    canEditInterview
                                        ? handleAddEditInterviewDialogOpen
                                        : undefined
                                }
                                handleOpenInterviewDetails={
                                    handleInterviewDetailsDialogOpen
                                }
                                dataCy="pastInterviews"
                            />
                        </Breakpoint>
                        <Breakpoint mobile>
                            <CandidateJobInterviewListMobile
                                interviewList={pastInterviews}
                                handleOpenEditInterview={
                                    canEditInterview
                                        ? handleAddEditInterviewDialogOpen
                                        : undefined
                                }
                                handleOpenInterviewDetails={
                                    handleInterviewDetailsDialogOpen
                                }
                                data-cy="pastInterviews"
                            />
                        </Breakpoint>
                    </div>
                )}
                {isAddEditInterviewModalOpen ? (
                    <CandidateAddEditInterviewDialog
                        onCancel={handleAddEditInterviewDialogClose}
                        onComplete={handleInterviewComplete}
                        onSubmit={onSubmitInterview}
                        candidateInterview={{
                            candidateFullName: candidateFullName,
                            candidateInterviewId:
                                currentInterview?.candidateInterviewId,
                            interviewTypeId: currentInterview?.interviewTypeId,
                            interviewDate: currentInterview?.interviewDate,
                            interviewTime: currentInterview?.interviewTime,
                            durationHours: currentInterview?.durationHours,
                            durationMinutes: currentInterview?.durationMinutes,
                            timeZoneId: currentInterview?.timeZoneId,
                            interviewStatusTypeId:
                                currentInterview?.interviewStatusTypeId,
                            interviewDetails:
                                currentInterview?.interviewDetails,
                            sendNotification:
                                currentInterview?.sendNotification,
                        }}
                    />
                ) : null}
                {isInterviewDetailsModalOpen ? (
                    <CandidateInterviewDetailsDialog
                        candidateInterview={{
                            candidateFullName: candidateFullName,
                            candidateInterviewId:
                                currentInterview?.candidateInterviewId,
                            interviewTypeId: currentInterview?.interviewTypeId,
                            startDateTime: currentInterview?.startDateTime,
                            durationHours: currentInterview?.durationHours,
                            durationMinutes: currentInterview?.durationMinutes,
                            timeZoneId: currentInterview?.timeZoneId,
                            interviewStatusTypeId:
                                currentInterview?.interviewStatusTypeId,
                            interviewDetails:
                                currentInterview?.interviewDetails,
                            sendNotification:
                                currentInterview?.sendNotification,
                        }}
                        jobName={jobName}
                        handleSend={
                            handleTransitionFromDetailsToNotificationDialogs
                        }
                        onClose={handleInterviewDetailsDialogClose}
                    />
                ) : null}
                {isInterviewNotificationDialogOpen ? (
                    <CandidateInterviewNotificationDialog
                        candidateFullName={candidateFullName}
                        candidateFirstName={candidateFirstName}
                        candidateUserId={candidateUserId}
                        candidateRoleName={candidateRoleName}
                        jobName={jobName}
                        jobId={jobId}
                        companyName={companyName}
                        companyBackground={companyBackground}
                        interviewId={currentInterview?.candidateInterviewId}
                        interviewStartDateTime={currentInterview?.startDateTime}
                        interviewTimeZoneId={currentInterview?.timeZoneId}
                        interviewDurationHours={currentInterview?.durationHours}
                        interviewDurationMinutes={
                            currentInterview?.durationMinutes
                        }
                        interviewDetails={currentInterview?.interviewDetails}
                        candidateManagedBy={candidateManagedBy}
                        jobJobManager={jobJobManager}
                        handleSendManualInterviewNotification={
                            handleSendManualInterviewNotification
                        }
                        onClose={handleInterviewNotificationDialogClose}
                        isCandidateLoading={isCandidateLoading}
                    />
                ) : null}
            </div>
        </LayoutContainer>
    );
};
